import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BookOpen, MapPin, PlusCircle, ClipboardList, User, LogOut, Menu, X, Shield, Coins, Sparkles, MessageSquareHeart, BookOpenCheck } from "lucide-react";
import { useState } from "react";
import NotificationsBell from "@/components/NotificationsBell";
import MessagesBell from "@/components/MessagesBell";

const links = [
  { to: "/browse", label: "Browse", icon: BookOpen, testid: "nav-browse" },
  { to: "/list", label: "List a Book", icon: PlusCircle, testid: "nav-list" },
  { to: "/ledger", label: "My Ledger", icon: ClipboardList, testid: "nav-ledger" },
];

const sideOnlyLinks = [
  { to: "/reading-tracker", label: "Reading Tracker", icon: BookOpenCheck, testid: "nav-reading-tracker" },
  { to: "/pro", label: "Pro Plan", icon: Sparkles, testid: "nav-pro" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-40 bg-amber-50 border-b border-amber-100/70">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="nav-logo">
          <img
            src="/logo.png"
            alt="Junior Shelf logo"
            className="h-14 w-auto object-contain group-hover:rotate-[-4deg] transition-transform"
          />
          <div className="leading-tight">
            <div className="font-display text-lg font-semibold text-slate-900">Junior Shelf</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-orange-500 font-bold">share · borrow · read</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-orange-500 text-white shadow-sm shadow-orange-500/30"
                    : "text-slate-700 hover:bg-amber-100"
                }`
              }
            >
              <l.icon size={16} strokeWidth={2.2} />
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {user ? (
            <>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="nav-admin"
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-100 text-orange-800 text-xs font-bold uppercase tracking-wider hover:bg-orange-200 transition-colors"
                >
                  <Shield size={12} strokeWidth={2.4} /> Admin
                </Link>
              )}
              {typeof user.credits === "number" && (
                <Link
                  to="/ledger"
                  data-testid="nav-credits"
                  title={`You have ${user.credits} credits`}
                  className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-teal-50 border border-teal-100 text-teal-800 text-xs font-bold hover:bg-teal-100 transition-colors"
                >
                  <Coins size={13} strokeWidth={2.4} />
                  <span data-testid="nav-credits-value">{user.credits}</span>
                  <span className="uppercase tracking-wider text-[10px] text-teal-600">credits</span>
                </Link>
              )}
              <NotificationsBell />
              <MessagesBell />
              <Link
                to="/profile"
                data-testid="nav-profile"
                className="hidden sm:flex items-center gap-2 pl-2 pr-4 py-1.5 rounded-full bg-white border border-slate-200 hover:border-orange-300 transition-colors"
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-teal-500 text-white flex items-center justify-center text-xs font-bold">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </div>
                )}
                <span className="text-sm font-semibold text-slate-800 max-w-[110px] truncate">{user.name}</span>
              </Link>
              <button
                onClick={handleLogout}
                className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full text-slate-500 hover:text-orange-600 hover:bg-amber-100 transition-colors"
                data-testid="nav-logout"
                aria-label="Log out"
              >
                <LogOut size={18} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" onClick={() => window.gtag && window.gtag('event','login_button_click',{location:'navbar_desktop'})} className="hidden sm:inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-slate-700 hover:bg-amber-100" data-testid="nav-login">
                Log in
              </Link>
              <Link to="/register" className="btn-primary text-sm py-2 px-5" data-testid="nav-signup">
                Join
              </Link>
            </>
          )}
          <button
            className="lg:hidden w-9 h-9 rounded-full bg-white border border-slate-200 flex items-center justify-center"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            data-testid="nav-mobile-toggle"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-amber-100 px-4 py-3 space-y-1" data-testid="nav-mobile-menu">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold ${
                  isActive ? "bg-orange-100 text-orange-700" : "text-slate-700 hover:bg-amber-50"
                }`
              }
              data-testid={`mobile-${l.testid}`}
            >
              <l.icon size={18} />
              {l.label}
            </NavLink>
          ))}
          {sideOnlyLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold ${
                  isActive ? "bg-orange-100 text-orange-700" : "text-slate-700 hover:bg-amber-50"
                }`
              }
              data-testid={`mobile-${l.testid}`}
            >
              <l.icon size={18} />
              {l.label}
            </NavLink>
          ))}
          <NavLink
            to="/feedback"
            onClick={() => setOpen(false)}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold ${
                isActive ? "bg-orange-100 text-orange-700" : "text-slate-700 hover:bg-amber-50"
              }`
            }
            data-testid="mobile-nav-feedback"
          >
            <MessageSquareHeart size={18} />
            Share feedback
          </NavLink>
          {user ? (
            <>
              {user.role === "admin" && (
                <NavLink to="/admin" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold bg-orange-50 text-orange-700 hover:bg-orange-100" data-testid="mobile-nav-admin">
                  <Shield size={18} /> Admin control
                </NavLink>
              )}
              <NavLink to="/profile" onClick={() => setOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold text-slate-700 hover:bg-amber-50">
                <User size={18} /> Profile
              </NavLink>
              <button onClick={() => { setOpen(false); handleLogout(); }} className="w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-semibold text-rose-600 hover:bg-rose-50">
                <LogOut size={18} /> Log out
              </button>
            </>
          ) : (
            <div className="flex gap-2 pt-2">
              <Link to="/login" onClick={() => { setOpen(false); window.gtag && window.gtag('event','login_button_click',{location:'navbar_mobile'}); }} className="btn-outline flex-1 py-2">Log in</Link>
              <Link to="/register" onClick={() => setOpen(false)} className="btn-primary flex-1 py-2">Join</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
